import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  tableBodyClasses,
  tableCellClasses,
  tableContainerClasses,
  tableRowClasses,
} from "@mui/material";
import type { BaseTransfert } from "models";
import { grey } from "@mui/material/colors";
import { porteesUtil } from "utils";
import EauAssGepuPorteesAccordion from "./EauAssGepuPorteesAccordion";
import { EauContrats, GepuContrats, AssContrats } from "constants/EauAssContrats";
import type { EauAssGepuContrat } from "constants/EauAssContrats";
import Competence from "constants/Competence";
import HasCompetenceIcon from "./HasCompetenceIcon";

function getCompetenceContrats(competence: Competence): Array<EauAssGepuContrat> {
  switch (competence) {
    case Competence.EAU:
      return EauContrats;
    case Competence.ASS:
      return AssContrats;
    default:
      return GepuContrats;
  }
}

/**
 * Évalue le besoin d'afficher les portées d'une sous compétence.
 *
 * Cela permet notamment de traiter les cas historiques de sous compétence telles que ASS-AB-0 qui n'étaient
 * saisies qu'avec ce code sans le détail des portées, alors que l'US #17670 introduit les portées pour ces cas.
 *
 * @returns `true` lorsque la sous-compétence n'est pas dans ce cas historique,
 * où lorsque le code correspond à ce potentiel cas **ET** que des transferts avec les nouvelles portées existent.
 *
 * Sinon `false` lorsque c'est un cas historique et qu'aucun transfert avec les portées n'existent.
 */
function shouldDisplayPortees(
  competence: Competence,
  sousCompetence: EauAssGepuContrat,
  transferts: Array<BaseTransfert>
): boolean {
  return (
    !/(ASS|EAU)-(AB|AD|MO)-0/.test(`${competence}-${sousCompetence.code}-0`) ||
    transferts.some(
      (transfert) =>
        transfert.code.startsWith(`${competence}-${sousCompetence.code}-`) &&
        transfert.code !== `${competence}-${sousCompetence.code}-0`
    )
  );
}

interface EauAssGepuSousCompetencesAccordionDetailsProps {
  transferts: Array<BaseTransfert>;
}

function EauAssGepuSousCompetencesAccordionDetails({
  transferts,
}: Readonly<EauAssGepuSousCompetencesAccordionDetailsProps>): ReactNode {
  const [competence, setCompetence] = useState<Competence>(
    porteesUtil.getCompetence(transferts[0])
  );
  const [contrats, setContrats] = useState<Array<EauAssGepuContrat>>([]);

  useEffect(() => {
    const _competence = porteesUtil.getCompetence(transferts[0]);
    setCompetence(_competence);
    setContrats(getCompetenceContrats(_competence));
  }, [transferts]);

  return (
    <TableContainer
      sx={{
        [`&.${tableContainerClasses.root} .${tableBodyClasses.root} .${tableRowClasses.root}:last-child .${tableCellClasses.root}`]:
          { border: "none" },
      }}>
      <Table size="small">
        <TableBody>
          {contrats.map((sousCompetence) => {
            const hasSousCompetence = transferts.some(
              (transfert) => porteesUtil.getSousCompetenceId(transfert) === sousCompetence.code
            );
            const hasPorteeToDisplay = shouldDisplayPortees(competence, sousCompetence, transferts);
            return (
              <TableRow key={sousCompetence.code}>
                <TableCell width="40px" sx={{ verticalAlign: "baseline", paddingRight: 0 }}>
                  <HasCompetenceIcon checked={hasSousCompetence} />
                </TableCell>
                <TableCell>
                  <Grid container>
                    <Grid size={12}>
                      <Typography color={hasSousCompetence ? undefined : grey[400]}>
                        {porteesUtil.getSousCompetenceLabel(
                          `${competence}-${sousCompetence.code}-0`
                        )}
                      </Typography>
                    </Grid>
                    {hasPorteeToDisplay && sousCompetence.portees != null && (
                      <Grid size={12}>
                        <EauAssGepuPorteesAccordion
                          transferts={transferts}
                          porteeIds={sousCompetence.portees}
                          porteeAdditionnellesIds={sousCompetence.porteesAdditionnelles}
                          sousCompetenceId={sousCompetence.code}
                        />
                      </Grid>
                    )}
                  </Grid>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default EauAssGepuSousCompetencesAccordionDetails;
